import React, { useEffect, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle, faPlus } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { SUPPORT_NAV, PRIORITY_COLORS } from './nav';

interface Complaint {
    id: number; subject: string; description: string; severity: string; status: string;
    patient: string | null; clinic: string | null; assigned_to: string | null; assigned_to_id: number | null;
    resolution_notes: string; created: string; resolved_at: string | null;
}
interface Agent { id: number; full_name: string; }

const STATUS_COLORS: Record<string, string> = { submitted: 'warning', investigating: 'info', resolved: 'success', dismissed: 'secondary' };
const STATUSES = ['submitted', 'investigating', 'resolved', 'dismissed'];

const ComplaintsRegister: React.FC = () => {
    const { api } = useInternalApi();
    const [complaints, setComplaints] = useState<Complaint[]>([]);
    const [agents, setAgents] = useState<Agent[]>([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('');
    const [showCreate, setShowCreate] = useState(false);
    const [form, setForm] = useState({ subject: '', description: '', severity: 'medium' });
    const [viewing, setViewing] = useState<Complaint | null>(null);
    const [notes, setNotes] = useState('');

    useEffect(() => { document.title = 'Complaints Register - Uzima Internal'; }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (statusFilter) params.set('status', statusFilter);
            const res = await api.get(`admin-dashboard/support/complaints/?${params}`);
            if (res.data?.status === 'success') setComplaints(res.data.data || []);
        } catch { setComplaints([]); } finally { setLoading(false); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        api.get('admin-dashboard/support/agents/').then(r => { if (r.data?.status === 'success') setAgents(r.data.data || []); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submitCreate = async () => {
        if (!form.subject.trim() || !form.description.trim()) {
            Swal.fire({ icon: 'warning', title: 'Missing Fields', text: 'Subject and description are required.' });
            return;
        }
        try {
            const res = await api.post('admin-dashboard/support/complaints/', form);
            if (res.data?.status === 'success') {
                setShowCreate(false);
                setForm({ subject: '', description: '', severity: 'medium' });
                load();
            }
        } catch (err: any) {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || 'Could not log complaint.' });
        }
    };

    const patchComplaint = async (c: Complaint, payload: any) => {
        try {
            const res = await api.patch(`admin-dashboard/support/complaints/${c.id}/`, payload);
            if (res.data?.status === 'success') {
                if (viewing?.id === c.id) setViewing(res.data.data);
                load();
            }
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Could not update complaint.' });
        }
    };

    return (
        <InternalShell activeDashboard="support_center" subNav={SUPPORT_NAV}>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faExclamationCircle} className="me-2 text-primary" />Complaints Register</h2>
                    <p className="text-muted mb-0">Formal complaints and their investigation lifecycle</p>
                </div>
                <button className="btn btn-primary" onClick={() => setShowCreate(true)}><FontAwesomeIcon icon={faPlus} className="me-2" />Log Complaint</button>
            </div>

            <select className="form-select mb-3" style={{ maxWidth: 220 }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">All statuses</option>
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>

            <div className="card border-0 shadow-sm">
                <div className="card-body p-0">
                    {loading ? (
                        <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
                    ) : complaints.length === 0 ? (
                        <div className="text-center py-5 text-muted">No complaints on record.</div>
                    ) : (
                        <table className="table table-hover align-middle mb-0">
                            <thead className="table-light"><tr><th>Subject</th><th>Clinic</th><th>Severity</th><th>Status</th><th>Assigned</th><th>Filed</th></tr></thead>
                            <tbody>
                                {complaints.map(c => (
                                    <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => { setViewing(c); setNotes(c.resolution_notes); }}>
                                        <td>{c.subject}</td>
                                        <td>{c.clinic || '—'}</td>
                                        <td><span className={`badge bg-${PRIORITY_COLORS[c.severity] || 'secondary'}`}>{c.severity}</span></td>
                                        <td><span className={`badge bg-${STATUS_COLORS[c.status]}`}>{c.status}</span></td>
                                        <td>{c.assigned_to || <span className="text-muted">Unassigned</span>}</td>
                                        <td className="small">{new Date(c.created).toLocaleDateString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {showCreate && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Log a Complaint</h5>
                                <button type="button" className="btn-close" onClick={() => setShowCreate(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Subject</label>
                                    <input className="form-control" value={form.subject} onChange={(e) => setForm(f => ({ ...f, subject: e.target.value }))} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Description</label>
                                    <textarea className="form-control" rows={4} value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Severity</label>
                                    <select className="form-select" value={form.severity} onChange={(e) => setForm(f => ({ ...f, severity: e.target.value }))}>
                                        <option value="low">Low</option><option value="medium">Medium</option>
                                        <option value="high">High</option><option value="critical">Critical</option>
                                    </select>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setShowCreate(false)}>Cancel</button>
                                <button className="btn btn-primary" onClick={submitCreate}>Log Complaint</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {viewing && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{viewing.subject}</h5>
                                <button type="button" className="btn-close" onClick={() => setViewing(null)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="d-flex gap-2 mb-3">
                                    <span className={`badge bg-${PRIORITY_COLORS[viewing.severity]}`}>{viewing.severity}</span>
                                    <span className={`badge bg-${STATUS_COLORS[viewing.status]}`}>{viewing.status}</span>
                                    {viewing.clinic && <span className="badge bg-light text-dark">{viewing.clinic}</span>}
                                </div>
                                <p>{viewing.description}</p>

                                <div className="row g-3 mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label small">Status</label>
                                        <select className="form-select form-select-sm" value={viewing.status} onChange={(e) => patchComplaint(viewing, { status: e.target.value })}>
                                            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label small">Assigned to</label>
                                        <select className="form-select form-select-sm" value={viewing.assigned_to_id || ''} onChange={(e) => patchComplaint(viewing, { assigned_to_id: e.target.value || null })}>
                                            <option value="">Unassigned</option>
                                            {agents.map(a => <option key={a.id} value={a.id}>{a.full_name}</option>)}
                                        </select>
                                    </div>
                                </div>

                                <label className="form-label small">Resolution notes</label>
                                <textarea className="form-control" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
                                {viewing.resolved_at && <div className="form-text">Resolved {new Date(viewing.resolved_at).toLocaleString()}</div>}
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setViewing(null)}>Close</button>
                                <button className="btn btn-primary" onClick={() => patchComplaint(viewing, { resolution_notes: notes })}>Save Notes</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </InternalShell>
    );
};

export default ComplaintsRegister;
